import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money, StatusBadge, LineItemsEditor, computeTotals } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Loader2, Wallet, FileDown, Send, Smartphone } from "lucide-react";

export default function Invoices() {
  const { companyId, cur } = useCompany();
  const [items, setItems] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_id: "", due_date: "", lines: [{ product_id: "", name: "", qty: 1, unit_price: 0, tax_rate: 0 }] });
  const [pay, setPay] = useState({ amount: 0, method: "cash", provider: "orange_money" });

  const load = () => api.get("/invoices", { params: { company_id: companyId } }).then(({ data }) => setItems(data.invoices));
  useEffect(() => {
    if (!companyId) return;
    load();
    api.get("/customers", { params: { company_id: companyId } }).then(({ data }) => setCustomers(data.customers));
    api.get("/products", { params: { company_id: companyId } }).then(({ data }) => setProducts(data.products));
  }, [companyId]);

  const totals = computeTotals(form.lines);
  const create = async () => {
    if (!form.customer_id) return toast.error("Choisissez un client");
    setSaving(true);
    try { await api.post("/invoices", form, { params: { company_id: companyId } }); toast.success("Facture créée + écriture comptable générée");
      setOpen(false); setForm({ customer_id: "", due_date: "", lines: [{ product_id: "", name: "", qty: 1, unit_price: 0, tax_rate: 0 }] }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const openDetail = async (id) => {
    const { data } = await api.get(`/invoices/${id}`, { params: { company_id: companyId } });
    setDetail(data); setPay({ amount: Math.max(data.invoice.total - data.invoice.amount_paid, 0), method: "cash", provider: "orange_money" });
  };
  const recordPayment = async () => {
    try {
      const body = { amount: parseFloat(pay.amount), method: pay.method, provider: pay.method === "mobile_money" ? pay.provider : null };
      await api.post(`/invoices/${detail.invoice.id}/payments`, body, { params: { company_id: companyId } });
      toast.success("Paiement enregistré + réconcilié"); setDetail(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/invoices/${detail.invoice.id}/pdf`, { params: { company_id: companyId }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `${detail.invoice.number}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erreur PDF"); }
  };
  const sendWhatsapp = async () => {
    try { const { data } = await api.post(`/invoices/${detail.invoice.id}/whatsapp`, {}, { params: { company_id: companyId } });
      toast.success("Facture envoyée sur WhatsApp (simulation)"); if (data.wa_link) window.open(data.wa_link, "_blank");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const simMomo = async () => {
    try { await api.post(`/momo/simulate`, {}, { params: { company_id: companyId, invoice_id: detail.invoice.id, provider: "wave_money" } });
      toast.success("Paiement Mobile Money reçu et réconcilié (webhook simulé)"); setDetail(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Factures" subtitle="Émettez des factures et encaissez vos clients." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-invoice-button"><Plus className="w-4 h-4 mr-1" /> Nouvelle facture</Button></DialogTrigger>
          <DialogContent className="bg-popover max-w-3xl">
            <DialogHeader><DialogTitle className="font-display">Nouvelle facture</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Client *</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="invoice-customer-select">
                    <option value="">— Choisir —</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select></div>
                <div className="space-y-1"><Label>Échéance</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due-input" /></div>
              </div>
              <LineItemsEditor lines={form.lines} setLines={(l) => setForm({ ...form, lines: l })} products={products} />
              <div className="flex justify-end gap-6 text-sm border-t border-border pt-3">
                <span className="text-muted-foreground">Sous-total <b className="text-foreground"><Money value={totals.subtotal} cur={cur} /></b></span>
                <span className="text-muted-foreground">TVA <b className="text-foreground"><Money value={totals.tax} cur={cur} /></b></span>
                <span className="font-display font-bold text-base">Total <Money value={totals.total} cur={cur} /></span>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} className="rounded-full" data-testid="save-invoice-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer la facture"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />

      <Card className="rounded-md border-border shadow-sm overflow-hidden">
        {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState title="Aucune facture" hint="Créez votre première facture." />
          : <Table>
              <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Échéance</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Payé</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
              <TableBody>{items.map((i) => (
                <TableRow key={i.id} className="cursor-pointer" onClick={() => openDetail(i.id)} data-testid={`invoice-row-${i.id}`}>
                  <TableCell className="font-mono text-xs">{i.number}</TableCell>
                  <TableCell className="font-medium">{i.customer_name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{fmtDate(i.due_date)}</TableCell>
                  <TableCell className="text-right"><Money value={i.total} cur={cur} /></TableCell>
                  <TableCell className="text-right text-emerald-600"><Money value={i.amount_paid} cur={cur} /></TableCell>
                  <TableCell><StatusBadge status={i.status} /></TableCell>
                </TableRow>))}
              </TableBody>
            </Table>}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-popover max-w-2xl" data-testid="invoice-detail">
          {detail && <>
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2">{detail.invoice.number} <StatusBadge status={detail.invoice.status} /></DialogTitle></DialogHeader>
            <div className="text-sm text-muted-foreground">Client : <b className="text-foreground">{detail.invoice.customer_name}</b> · Échéance {fmtDate(detail.invoice.due_date)}</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={downloadPdf} data-testid="invoice-pdf-button"><FileDown className="w-3.5 h-3.5 mr-1" />PDF + QR</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={sendWhatsapp} data-testid="invoice-whatsapp-button"><Send className="w-3.5 h-3.5 mr-1" />WhatsApp</Button>
              {detail.invoice.status !== "paid" && detail.invoice.status !== "cancelled" &&
                <Button size="sm" variant="outline" className="rounded-full" onClick={simMomo} data-testid="invoice-momo-sim-button"><Smartphone className="w-3.5 h-3.5 mr-1" />Encaisser via Mobile Money</Button>}
            </div>
            <div className="border border-border rounded-md divide-y divide-border">
              {detail.invoice.lines.map((l, i) => (
                <div key={i} className="flex justify-between px-3 py-2 text-sm"><span>{l.qty} × {l.name}</span><Money value={l.line_total} cur={cur} /></div>
              ))}
              <div className="flex justify-between px-3 py-2 font-display font-bold"><span>Total</span><Money value={detail.invoice.total} cur={cur} /></div>
            </div>
            {detail.invoice.status !== "paid" && detail.invoice.status !== "cancelled" && (
              <Card className="p-4 border-border bg-secondary/40">
                <div className="font-semibold text-sm flex items-center gap-2 mb-3"><Wallet className="w-4 h-4 text-primary" /> Enregistrer un paiement</div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1"><Label>Montant</Label><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount-input" /></div>
                  <div className="space-y-1"><Label>Moyen</Label>
                    <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} data-testid="payment-method-select">
                      <option value="cash">Espèces</option><option value="bank">Banque</option><option value="mobile_money">Mobile Money</option>
                    </select></div>
                  {pay.method === "mobile_money" && <div className="space-y-1"><Label>Opérateur</Label>
                    <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={pay.provider} onChange={(e) => setPay({ ...pay, provider: e.target.value })} data-testid="payment-provider-select">
                      <option value="orange_money">Orange Money</option><option value="moov_money">Moov Money</option><option value="wave_money">Wave</option>
                    </select></div>}
                </div>
                <Button onClick={recordPayment} className="rounded-full mt-3 w-full" data-testid="record-payment-button">Encaisser</Button>
              </Card>)}
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
